import React, { useState, useEffect } from 'react'
import Modal from '@/v2/components/common/Modal'
import Grid from '@mui/material/Grid'
import TextField from '@/v2/components/common/TextField'
import Typography from '@/v2/components/common/Typography'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import SelectCalendar from '@/v2/components/common/SelectCalendar'
import { format } from 'date-fns'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useAddTask, useUpdateTask } from '@/v2/hooks/useTasks'
import PatientSearch from '@/v2/components/common/PatientSearch'
import UserSearch from '@/v2/components/common/UserSearch'
import Checkbox from '@mui/material/Checkbox'

const TaskForm = ({ isOpen, onClose, task }) => {
  const isEditMode = !!task
  const [taskType, setTaskType] = useState(
    task?.patient === 'Internal' ? 'Internal' : 'Patient'
  )
  const [formData, setFormData] = useState({
    title: '',
    patient: null,
    assignedById: null,
    dueDate: format(new Date(), 'yyyy-MM-dd'),
    completed: false,
  })
  const { onOpenSnackbar } = useSnackbarContext()
  const { addTask } = useAddTask()
  const { updateTask } = useUpdateTask(task?.id)

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        patient: task.patient === 'Internal' ? null : task.patient,
        assignedById: task.assignedBy?.id || null,
        assignedBy: task.assignedBy || null,
        dueDate: task.dueDate || format(new Date(), 'yyyy-MM-dd'),
        completed: task.completed || false,
      })
    }
  }, [task])

  const handleSubmit = async () => {
    try {
      const taskData = {
        title: formData.title,
        assignedById: formData.assignedById?.id || null,
        dueDate: formData.dueDate,
        completed: formData.completed,
        patientId: formData.patient?.id || null,
      }

      if (isEditMode) {
        await updateTask(taskData)
        onOpenSnackbar('Task updated successfully', 'success')
      } else {
        await addTask(taskData)
        onOpenSnackbar('Task created successfully', 'success')
      }
      onClose()
    } catch (error) {
      onOpenSnackbar(error.message || 'Error saving task', 'error')
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // If we're updating assignedById, also update assignedBy with the full user data
      ...(field === 'assignedById' && value
        ? {
            assignedBy: {
              id: value,
              firstName: value.firstName,
              lastName: value.lastName,
            },
          }
        : {}),
    }))
  }

  return (
    <Modal
      isOpen={isOpen}
      title={isEditMode ? 'Edit Task' : 'Add New Task'}
      maxWidth="sm"
      confirmLabel={isEditMode ? 'Save' : 'Submit'}
      onConfirm={handleSubmit}
      showConfirm={true}
      showClose={true}
      onClose={onClose}
      disabled={
        !formData.title || (taskType === 'Patient' && !formData.patient)
      }
    >
      <Grid container spacing={3}>
        {/* Task Type Selection */}
        <Grid item xs={12}>
          <RadioGroup
            row
            value={taskType}
            onChange={e => setTaskType(e.target.value)}
          >
            <FormControlLabel
              value="Patient"
              control={<Radio />}
              label={<Typography>Patient</Typography>}
            />
            <FormControlLabel
              value="Internal"
              control={<Radio />}
              label={<Typography>Internal</Typography>}
            />
          </RadioGroup>
        </Grid>

        {/* Patient Search */}
        {taskType === 'Patient' && (
          <Grid item xs={12}>
            <PatientSearch
              autoFocus
              showSearchIcon
              value={formData.patient}
              onChange={patient => handleChange('patient', patient)}
            />
          </Grid>
        )}

        {/* Task Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            placeholder="Write a task name..."
            value={formData.title}
            onChange={value => handleChange('title', value)}
          />
        </Grid>

        {/* Assigned To */}
        <Grid item xs={12}>
          <UserSearch
            onChange={user => handleChange('assignedById', user)}
            placeholder="Select a member of the staff (Optional)"
            value={formData.assignedBy}
          />
        </Grid>

        {/* Due Date */}
        <Grid item xs={12}>
          <SelectCalendar
            label="Due date"
            labelFormat="MMM dd, yyyy"
            displayFormat="MMM dd, yyyy"
            isBasicCalendar
            value={formData.dueDate || ''}
            onChange={value =>
              handleChange(
                'dueDate',
                value ? format(new Date(value), 'yyyy-MM-dd') : null
              )
            }
            placeholder="Select a due date (Optional)"
          />
        </Grid>

        {/* Completed Status */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.completed}
                onChange={e => handleChange('completed', e.target.checked)}
              />
            }
            label={<Typography>Completed</Typography>}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default TaskForm
